import { useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';

type SortableCollapsibleItemProps = {
	/** Primary label shown in the header */
	title: string;
	/** Optional secondary text shown in parens after the title */
	subtitle?: string;
	index: number;
	total: number;
	onMoveUp: () => void;
	onMoveDown: () => void;
	onDelete: () => void;
	children?: React.ReactNode;
};

/**
 * A collapsible list item with move-up / move-down / delete controls in the header.
 * Used inside SortableItemListDialog rows.
 */
export function SortableCollapsibleItem({
	title,
	subtitle,
	index,
	total,
	onMoveUp,
	onMoveDown,
	onDelete,
	children,
}: SortableCollapsibleItemProps) {
	const [expanded, setExpanded] = useState(false);
	const hasContent = !!children;

	return (
		<Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
			{/* Header */}
			<Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5, bgcolor: 'action.hover' }}>
				{hasContent && (
					<IconButton size="small" onClick={() => setExpanded((e) => !e)} title={expanded ? 'Collapse' : 'Expand'}>
						{expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
					</IconButton>
				)}
				<Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500 }}>
					{title}
					{subtitle && (
						<Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
							({subtitle})
						</Typography>
					)}
				</Typography>
				<IconButton size="small" onClick={onMoveUp} disabled={index === 0} title="Move up">
					<KeyboardArrowUpIcon fontSize="small" />
				</IconButton>
				<IconButton size="small" onClick={onMoveDown} disabled={index === total - 1} title="Move down">
					<KeyboardArrowDownIcon fontSize="small" />
				</IconButton>
				<IconButton size="small" onClick={onDelete} title="Delete">
					<DeleteIcon fontSize="small" />
				</IconButton>
			</Stack>

			{/* Collapsible content */}
			{hasContent && (
				<Collapse in={expanded}>
					<Stack spacing={2} sx={{ p: 2 }}>
						{children}
					</Stack>
				</Collapse>
			)}
		</Box>
	);
}
